import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { publicApi } from '../lib/api';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';

// Step components
import RatingStep from './funnel/RatingStep';
import PositiveStep from './funnel/PositiveStep';
import CategoriesStep from './funnel/CategoriesStep';
import AnalysisStep from './funnel/AnalysisStep';
import MirrorStep from './funnel/MirrorStep';
import FinishStep from './funnel/FinishStep';
import DoneStep from './funnel/DoneStep';

const CATEGORIES = [
  { key: 'communication', label: 'Kommunikation' },
  { key: 'service', label: 'Service' },
  { key: 'billing', label: 'Abrechnung' },
  { key: 'response_time', label: 'Reaktionszeit' },
  { key: 'competence', label: 'Kompetenz' },
];

const STEP_PROGRESS = {
  rating: 15, 'negative-categories': 35, mirror: 55, 'negative-analysis': 70,
  positive: 80, finish: 90, done: 100,
};

const STEP_LABELS = {
  rating: 'Bewertung', 'negative-categories': 'Details', mirror: 'Ihr Ergebnis',
  'negative-analysis': 'Prüfung', positive: 'Übertragen', finish: 'Abschluss', done: 'Fertig',
};

// Hex → "H S% L%" (Format der Tailwind/shadcn CSS-Variablen).
// Damit schlagen die Tenant-Farben auf bg-primary, text-primary usw. im Funnel durch.
function hexToHslParts(hex) {
  if (typeof hex !== 'string') return null;
  let h = hex.trim().replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;

  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let hue = 0, sat = 0;

  if (max !== min) {
    const d = max - min;
    sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r)      hue = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) hue = ((b - r) / d + 2) / 6;
    else                hue = ((r - g) / d + 4) / 6;
  }
  return `${Math.round(hue * 360)} ${Math.round(sat * 100)}% ${Math.round(l * 100)}%`;
}

function buildFunnelTheme(tenant) {
  if (!tenant) return undefined;
  const style = {};
  const primary = hexToHslParts(tenant.brand_color);
  const secondary = hexToHslParts(tenant.secondary_color);
  if (primary) {
    style['--primary'] = primary;
    style['--ring'] = primary;
  }
  if (secondary) style['--accent'] = secondary;
  if (tenant.background_color) style.backgroundColor = tenant.background_color;
  return Object.keys(style).length ? style : undefined;
}

// Stable visitor ID for live ping
const VISITOR_ID = (() => {
  let id = sessionStorage.getItem('immo_vid');
  if (!id) { id = Math.random().toString(36).slice(2, 12); sessionStorage.setItem('immo_vid', id); }
  return id;
})();

export default function ReviewFunnel() {
  const { tenantSlug, linkSlug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [tenant, setTenant] = useState(null);
  const [targetPlatforms, setTargetPlatforms] = useState(null);
  const [reviewId, setReviewId] = useState(null);
  const [step, setStep] = useState('rating');
  const [selectedRating, setSelectedRating] = useState(null);

  // Positive flow
  const [suggestion, setSuggestion] = useState('');
  const [regenerating, setRegenerating] = useState(false);

  // Negative flow
  const [categories, setCategories] = useState(
    CATEGORIES.map(c => ({ ...c, rating: 3, comment: '' }))
  );
  const [refinedAvg, setRefinedAvg] = useState(null);
  const [converted, setConverted] = useState(false);
  const [positiveVariant, setPositiveVariant] = useState('positive'); // positive | converted | critical
  const [afterAnalysis, setAfterAnalysis] = useState(null);           // wohin nach der Sachlichkeitsprüfung
  const [doneHeadline, setDoneHeadline] = useState('Bewertung abgeschlossen');
  const [freeText, setFreeText] = useState('');
  const [riskAnalysis, setRiskAnalysis] = useState(null);
  const [rewrittenText, setRewrittenText] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [useRewritten, setUseRewritten] = useState(false);

  // Finish
  const [email, setEmail] = useState('');
  const [finishing, setFinishing] = useState(false);

  // Live presence ping — every 20s while funnel is open
  useEffect(() => {
    if (!tenantSlug || !linkSlug) return;
    publicApi.pingLive(tenantSlug, linkSlug, VISITOR_ID).catch(() => {});
    const interval = setInterval(() => {
      publicApi.pingLive(tenantSlug, linkSlug, VISITOR_ID).catch(() => {});
    }, 20000);
    return () => clearInterval(interval);
  }, [tenantSlug, linkSlug]);

  useEffect(() => {
    const loadTenantInfo = async () => {
      try {
        const response = await publicApi.getTenantLinkInfo(tenantSlug, linkSlug);
        setTenant(response.data.tenant);
        if (response.data.link?.target_platforms) {
          setTargetPlatforms(response.data.link.target_platforms);
        }
      } catch {
        setError('Dieser Bewertungslink ist nicht verfügbar.');
      } finally {
        setLoading(false);
      }
    };
    loadTenantInfo();
  }, [tenantSlug, linkSlug]);

  useEffect(() => {
    const handleUnload = () => {
      if (reviewId && step && step !== 'done') {
        publicApi.abandonReview(reviewId, step);
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [reviewId, step]);

  const handleRatingSelect = async (rating) => {
    setSelectedRating(rating);
    // Sterne der Detailabfrage mit dem Impuls-Wert vorbelegen (#23)
    setCategories(prev => prev.map(c => ({ ...c, rating })));
    setPositiveVariant('positive');
    setLoading(true);
    try {
      const response = await publicApi.startReview({
        tenant_slug: tenantSlug, link_slug: linkSlug, overall_rating: rating
      });
      setReviewId(response.data.review_id);
      if (response.data.path === 'positive') {
        setSuggestion(response.data.suggestion || '');
        setStep('positive');
      } else {
        setStep('negative-categories');
      }
    } catch {
      toast.error('Fehler beim Starten der Bewertung');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('In Zwischenablage kopiert');
    } catch {
      toast.info('Bitte den Text manuell kopieren');
    }
    if (reviewId) {
      publicApi.trackEvent(reviewId, 'copied_suggestion').catch(() => {});
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const response = await publicApi.regenerateSuggestion(reviewId);
      setSuggestion(response.data.suggestion);
      toast.success('Neuer Textvorschlag generiert');
    } catch { toast.error('Fehler beim Generieren'); }
    finally { setRegenerating(false); }
  };

  const handleRefineSuggestion = async (instruction, currentText) => {
    setRegenerating(true);
    try {
      const response = await publicApi.refineSuggestion(reviewId, instruction, currentText);
      setSuggestion(response.data.suggestion);
    } catch { toast.error('Fehler beim Verfeinern'); }
    finally { setRegenerating(false); }
  };

  const handleGenerateTitle = async (currentText) => {
    try {
      const response = await publicApi.generateTitle(reviewId, currentText);
      return response.data.title;
    } catch {
      toast.error('Fehler beim Generieren des Titels');
      return '';
    }
  };

  const handlePlatformChoice = async (platform) => {
    try {
      await publicApi.chooseExternal(reviewId, platform);
    } catch { console.error('Platform choice error'); }
  };

  const handlePositiveFinish = async (contactWish) => {
    setFinishing(true);
    try {
      await publicApi.finishReview(reviewId, email || null, !!contactWish);
      setStep('done');
    } catch { toast.error('Fehler beim Abschließen'); }
    finally { setFinishing(false); }
  };

  const handleSubmitCategories = async () => {
    setAnalyzing(true);
    try {
      const res = await publicApi.updateCategories(reviewId, {
        categories: categories.map(c => ({ key: c.key, label: c.label, rating: c.rating, comment: null })),
        free_text: null,
      });
      setSuggestion(res.data.suggestion || '');
      setRefinedAvg(res.data.refined_avg);
      setConverted(!!res.data.converted);
      setStep('mirror');
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setAnalyzing(false); }
  };

  // Spiegel: neuen Vorschlag auf Basis der Kategorien
  const handleMirrorRegenerate = async () => {
    setRegenerating(true);
    try {
      const res = await publicApi.updateCategories(reviewId, {
        categories: categories.map(c => ({ key: c.key, label: c.label, rating: c.rating, comment: null })),
        free_text: null,
      });
      setSuggestion(res.data.suggestion || '');
    } catch { toast.error('Fehler beim Generieren'); }
    finally { setRegenerating(false); }
  };

  // Text sichern und ggf. Sachlichkeitsprüfung — nur wenn der Text auffällig ist
  const persistTextAndRoute = async (target, { skipAnalysis = false } = {}) => {
    setAnalyzing(true);
    try {
      await publicApi.updateText(reviewId, suggestion || null);
      setFreeText(suggestion || '');
    } catch { toast.error('Fehler beim Speichern'); setAnalyzing(false); return; }

    // Prüfung ist optional — ein Fehler (Rate-Limit, KI) darf den Abschluss nicht blockieren
    if (!skipAnalysis && (suggestion || '').trim()) {
      try {
        const analysisResponse = await publicApi.analyzeRisk(reviewId);
        setRiskAnalysis(analysisResponse.data);
        if ((analysisResponse.data?.risk_score || 0) > 30) {
          setAfterAnalysis(() => target);
          setAnalyzing(false);
          setStep('negative-analysis');
          return;
        }
      } catch { /* weiter ohne Prüfung */ }
    }
    setAnalyzing(false);
    target();
  };

  const handleRewrite = async () => {
    setRewriting(true);
    try {
      const res = await publicApi.rewriteText(reviewId);
      setRewrittenText(res.data.rewritten_text || '');
      setUseRewritten(true);
      toast.success('Text wurde sachlich umformuliert');
    } catch { toast.error('Fehler beim Umformulieren'); }
    finally { setRewriting(false); }
  };

  const goPositiveEnd = (variant) => { setPositiveVariant(variant); setStep('positive'); };

  // Spiegel → gewandelt: direkt in den positiven Abschluss
  const handleMirrorContinuePositive = () => persistTextAndRoute(() => goPositiveEnd('converted'), { skipAnalysis: true });

  // Spiegel → Weiche
  const handleMirrorChoice = async (choice) => {
    try { await publicApi.setReaction(reviewId, choice); } catch { /* nicht blockierend */ }
    if (choice === 'send_to_hv') {
      setDoneHeadline('Feedback übermittelt');
      persistTextAndRoute(() => setStep('finish'));
    } else {
      persistTextAndRoute(() => goPositiveEnd('critical'));
    }
  };

  // Nach der Sachlichkeitsprüfung: ggf. neutralen Text übernehmen, dann weiter
  const handleAnalysisNext = async () => {
    if (useRewritten && rewrittenText) {
      setSuggestion(rewrittenText);
      try { await publicApi.updateText(reviewId, rewrittenText); } catch {}
    }
    const next = afterAnalysis;
    setAfterAnalysis(null);
    if (next) next(); else setStep('finish');
  };

  const handleFinish = async () => {
    setFinishing(true);
    try {
      await publicApi.finishReview(reviewId, email || null);
      setStep('done');
    } catch { toast.error('Fehler beim Abschließen'); }
    finally { setFinishing(false); }
  };

  if (loading && !tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Link nicht verfügbar</h1>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Link to="/"><Button variant="secondary">Zur Startseite</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className={`funnel-container ${tenant?.background_color ? '' : 'bg-muted/30'}`}
      style={buildFunnelTheme(tenant)}
    >
      <header className="bg-background border-b border-border px-5 py-5">
        <div className="max-w-xl mx-auto text-center">
          {/* Logo — zentriert, prominent */}
          <div className="flex justify-center mb-2.5">
            {tenant?.logo_url ? (
              <img
                src={tenant.logo_url}
                alt={tenant.display_name}
                className="h-20 max-w-[240px] w-auto object-contain"
              />
            ) : (
              <img src="/logo.png" alt="Immoverita" className="h-14 w-auto object-contain" />
            )}
          </div>
          <p className="font-semibold text-base leading-tight">{tenant?.display_name}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Ihre Meinung ist uns wichtig</p>

          {/* Fortschrittsbalken — nur auf Zwischenschritten */}
          {!['rating', 'negative-categories', 'done'].includes(step) && (
            <div className="flex items-center gap-3 mt-3 max-w-sm mx-auto">
              <Progress value={STEP_PROGRESS[step] || 0} className="flex-1 h-1.5" />
              <span className="text-xs text-muted-foreground whitespace-nowrap">{STEP_LABELS[step] || ''}</span>
            </div>
          )}
        </div>
      </header>

      <div className="funnel-content py-6 px-4 sm:px-6">
        <div className="max-w-xl mx-auto">
          <AnimatePresence mode="wait">
            {step === 'rating' && (
              <RatingStep tenant={tenant} selectedRating={selectedRating} loading={loading} onSelect={handleRatingSelect} />
            )}
            {step === 'positive' && (
              <PositiveStep
                selectedRating={selectedRating}
                refinedAvg={refinedAvg}
                variant={positiveVariant}
                emailFirst={positiveVariant === 'critical'}
                suggestion={suggestion} setSuggestion={setSuggestion}
                onCopy={handleCopy}
                onRegenerate={positiveVariant === 'positive' ? handleRegenerate : handleMirrorRegenerate}
                onRefineSuggestion={handleRefineSuggestion}
                onGenerateTitle={handleGenerateTitle}
                regenerating={regenerating}
                onPlatformChoice={handlePlatformChoice}
                onBack={positiveVariant === 'positive' ? () => { setSelectedRating(null); setStep('rating'); } : () => setStep('mirror')}
                targetPlatforms={targetPlatforms}
                platformUrls={tenant?.platform_urls || {}}
                email={email} setEmail={setEmail}
                onFinish={handlePositiveFinish} finishing={finishing}
              />
            )}
            {step === 'negative-categories' && (
              <CategoriesStep
                categories={categories}
                onCategoryRating={(key, r) => setCategories(prev => prev.map(c => c.key === key ? { ...c, rating: r } : c))}
                onSubmit={handleSubmitCategories} analyzing={analyzing}
                onBack={() => { setSelectedRating(null); setStep('rating'); }}
              />
            )}
            {step === 'mirror' && (
              <MirrorStep
                impulse={selectedRating} refinedAvg={refinedAvg} converted={converted}
                suggestion={suggestion} setSuggestion={setSuggestion}
                onRegenerate={handleMirrorRegenerate} regenerating={regenerating}
                onContinuePositive={handleMirrorContinuePositive}
                onChoice={handleMirrorChoice} submitting={analyzing}
                onBack={() => setStep('negative-categories')}
              />
            )}
            {step === 'negative-analysis' && (
              <AnalysisStep
                riskAnalysis={riskAnalysis} freeText={freeText}
                rewrittenText={rewrittenText} useRewritten={useRewritten}
                setUseRewritten={setUseRewritten} onRewrite={handleRewrite} rewriting={rewriting}
                onBack={() => { setAfterAnalysis(null); setStep('mirror'); }} onNext={handleAnalysisNext}
              />
            )}
            {step === 'finish' && (
              <FinishStep
                email={email} setEmail={setEmail} onFinish={handleFinish} finishing={finishing}
                onBack={() => setStep('mirror')}
              />
            )}
            {step === 'done' && (
              <DoneStep email={email} reviewId={reviewId} tenant={tenant} headline={doneHeadline} />
            )}
          </AnimatePresence>
        </div>
      </div>

      <footer className="bg-background border-t border-border py-5 text-center">
        {/* Legal links des Tenants */}
        {tenant?.legal_links && Object.values(tenant.legal_links).some(Boolean) && (
          <nav className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground mb-3">
            {tenant.legal_links.impressum && (
              <a href={tenant.legal_links.impressum} target="_blank" rel="noopener noreferrer"
                className="hover:text-foreground transition-colors">Impressum</a>
            )}
            {tenant.legal_links.datenschutz && (
              <a href={tenant.legal_links.datenschutz} target="_blank" rel="noopener noreferrer"
                className="hover:text-foreground transition-colors">Datenschutz</a>
            )}
            {tenant.legal_links.agb && (
              <a href={tenant.legal_links.agb} target="_blank" rel="noopener noreferrer"
                className="hover:text-foreground transition-colors">AGB</a>
            )}
          </nav>
        )}
        <p className="text-xs text-muted-foreground">
          Powered by <Link to="/" className="text-primary hover:underline">Immoverita</Link>
          <span className="mx-1.5 opacity-40">·</span>
          <Link to="/impressum" className="hover:text-foreground">Impressum</Link>
          <span className="mx-1.5 opacity-40">·</span>
          <Link to="/datenschutz" className="hover:text-foreground">Datenschutz</Link>
          <span className="mx-1.5 opacity-40">·</span>
          <Link to="/ki-transparenz" className="hover:text-foreground">KI-Transparenz</Link>
        </p>
      </footer>
    </div>
  );
}
